import el from "@cypherpotato/el";
import { api } from "../api";
import { FormControl, Select, TextArea } from "../components/form";
import { Paragraph } from "../components/text";
import { displayAlert } from "./alert";
import { CommandBar } from "../components/command-bar";
import { CodeEditor } from "../components/code-editor";
import { Section } from "../components/layout";
import { Dropdown, DropdownDivider, DropdownItem } from "../components/dropdown";
import { randomId } from "../util";
import { Icon } from "../components/icon";
import { runOperation } from "./operation";
import { showFlyout } from "./flyout";
import { PrimaryButton } from "../components/button";
import { showErrorToast, showSuccessToast } from "./toast";
import { Constants } from "../constants";

export async function requestAiEdit({ inputData, subject, description, responseSchema = null }) {

    return new Promise(async (resolve, reject) => {

        const baseId = "_base_" + randomId();
        const models = await api.getJson("/api/v1/information/models.json");

        const controls = el("div",
            Section(
                Paragraph(description)
            ),
            Section(
                CommandBar(
                    FormControl("Model",
                        Select({
                            id: baseId + "-model",
                            itemsRaw: models.data
                                .map(provider => el("optgroup",
                                    { label: provider.provider },
                                    provider.models.map(model => el("option", { value: model.name, selected: model.name == Constants.DefaultAiEditModelName }, model.name))
                                ))
                        })
                    ),
                    FormControl("Reasoning effort",
                        Select({
                            id: baseId + "-reasoning-effort",
                            value: "",
                            items: Constants.NullableReasoningEffortSelectItems.map(item => ({
                                ...item,
                                value: item.value ?? ""
                            }))
                        })
                    ),
                    Dropdown("Predefined prompts",
                        DropdownItem("Optimize", Icon("flashlight-line"), {
                            onClick() {
                                setPredefinedPrompt(`Optimize this ${subject} for better performance and effectiveness for LLMs.` + "\n\n" +
                                    `Apply corrections, organization, normalization, and improve the concept, objective, and format for this ${subject}.`)
                            }
                        }),
                        DropdownItem("Organize", Icon("book-shelf-line"), {
                            onClick() {
                                setPredefinedPrompt(`Organize and normalize this ${subject} into markdown sections, categorizing them by purpose, subject, and objective. Separate the section hierarchy with appropriate titles and subtitles.`)
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Improve conciseness", Icon("lightbulb-line"), {
                            onClick() {
                                setPredefinedPrompt(`Improve the concision of this ${subject}, so that it is clearer what it means.`)
                            }
                        }),
                        DropdownItem("Create examples", Icon("checkbox-multiple-line"), {
                            onClick() {
                                setPredefinedPrompt(`Create 2 or 3 examples to guide the LLM to respond based on this ${subject}.`)
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Expand", Icon("expand-diagonal-line"), {
                            onClick() {
                                setPredefinedPrompt(`Expand this ${subject}, so that it doesn't lose its meaning with the original content, but rather encompasses more situations, scenarios, and details.`)
                            }
                        }),
                        DropdownItem("Shrink", Icon("collapse-diagonal-line"), {
                            onClick() {
                                setPredefinedPrompt(`Shrink this ${subject}, so that it doesn't lose its original meaning or purpose.`)
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Translate to English", Icon("earth-line"), {
                            onClick() {
                                setPredefinedPrompt(`Translate this ${subject} to English.`);
                            }
                        }),
                        DropdownItem("Fix grammar", Icon("chat-check-line"), {
                            onClick() {
                                setPredefinedPrompt(`Fix and improve the grammar and spelling of this ${subject}.`);
                            }
                        }),
                    )
                )
            ),
            Section(
                TextArea(`${baseId}-code-editor`, null, {
                    rows: 3,
                    placeholder: "Please, improve the consistency and organize my instructions so that ..."
                })
            )
        );

        function setPredefinedPrompt(prompt) {
            controls.querySelector(`#${baseId}-code-editor`).value = prompt;
        }

        showFlyout(
            { style: { width: "700px" } },
            Section(
                controls
            ),
            Section(
                PrimaryButton("Request edit",
                    {
                        onClick() {

                            const prompt = controls.querySelector(`#${baseId}-code-editor`).value;
                            if (!prompt?.trim()) {
                                showErrorToast("Please, enter a prompt.", "error");
                                return;
                            }

                            runOperation("Generating content...", async () => {
                                const result = await api.postJson("/v1/chat/completions", {
                                    model: controls.querySelector(`#${baseId}-model`).value,
                                    reasoning_effort: controls.querySelector(`#${baseId}-reasoning-effort`).value || null,
                                    messages: [
                                        { role: "system", content: "You are editing a piece of information. You are receiving the data for editing in a JSON snippet and must respond with the edited data in the output JSON." },
                                        { role: "user", content: prompt },
                                        inputData && { role: "user", content: JSON.stringify(inputData) },
                                    ],
                                    json_only: true,
                                    response_schema: responseSchema || {
                                        type: "object",
                                        properties: {
                                            generatedContent: {
                                                type: "string",
                                                description: "Generated content based on the user's request."
                                            }
                                        }
                                    }
                                });

                                if (responseSchema) {
                                    resolve(result)
                                } else {
                                    resolve(result.generatedContent);
                                }
                            });
                        }
                    }
                )
            )
        );
    })
}
